import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useAtomValue, useSetAtom } from 'jotai';
import { openSnackbarAdviseAtom, userBranchesAtom } from '../../../Atoms';
import useAPI from '../../../api';
import FiltersNavbar from '../../Dashboard/FiltersNavbar';
import PivotWrapper from '../PivotWrapper';
import {
  getMetadataOutbound,
  DEFAULT_SLICE_CONFIGURATION_OUTBOUND,
} from '../utils/constant';
import CountersWrapper from './Components/CountersWrapper';
import { FullPageFlexBox, FullSizeColumn } from '../../../Components/FlexBox';
import {
  formatPayloadDate,
  getThisMonthRange,
  getTodayRange,
  getYesterdayRange,
  getThisWeekRange,
  getLastWeekRange,
} from '../../../Helpers/dates';

const payloadPeriod = function payloadPeriod(per, start, end) {
  let returnRange = [start, end];
  if (per === 'range') return returnRange;
  else if (per === 'month') returnRange = getThisMonthRange();
  else if (per === 'today') returnRange = getTodayRange();
  else if (per === 'yesterday') returnRange = getYesterdayRange();
  else if (per === 'week') returnRange = getThisWeekRange();
  else if (per === 'lastWeek') returnRange = getLastWeekRange();
  return returnRange;
};

const PivotOutboundReportPage = function PivotOutboundReportPage({
  userRoles,
}) {
  const API = useAPI();
  const intl = useIntl();
  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);

  const userBranches = useAtomValue(userBranchesAtom);

  const isOperator = Boolean(
    userRoles?.find(
      ({ roleId }) => roleId === 'OPERATOR' || roleId === 'LIMITEDOPERATOR'
    )
  );

  const [chartView, setChartView] = useState(
    isOperator
      ? true
      : localStorage.getItem('outboundPivotChartView') === 'true'
  );
  const [navbarOpen, setNavbarOpen] = useState(true);
  const [currentFilters, setCurrentFilters] = useState(null);
  const [pivotData, setPivotData] = useState(null);
  const [branchCounters, setBranchCounters] = useState(null);
  const [userCounters, setUserCounters] = useState(null);
  const [sectionsOptions, setSectionsOptions] = useState([]);
  const [sectionCounters, setSectionCounters] = useState(null);

  const handleToggleNavbar = () => {
    setNavbarOpen(!navbarOpen);
  };

  // Load sections and initialize filters from localStorage
  useEffect(() => {
    API.getWaveSections(
      (res) => {
        setSectionsOptions(res);

        // Initialize filters from localStorage or defaults
        const savedFilters = localStorage.getItem('outboundPivotFilters');
        if (savedFilters) {
          setCurrentFilters(JSON.parse(savedFilters));
        } else {
          setCurrentFilters({
            periodName: 'today',
            startDate: new Date().toISOString(),
            endDate: new Date().toISOString(),
            grouping: { id: 'D', label: intl.messages.DAILY },
            branches:
              userBranches.length > 0 ? userBranches.map(({ id }) => id) : [],
            operator: null,
            sections: res.map(({ id }) => id),
          });
        }
      },
      () => {
        setSectionsOptions([]);
      }
    );
  }, []);

  useEffect(() => {
    if (!currentFilters || sectionsOptions.length === 0) return;

    const [start, end] = payloadPeriod(
      currentFilters?.periodName,
      currentFilters?.startDate,
      currentFilters?.endDate
    );

    //if WAVEPERIOD has Value uses this API call
    if (
      currentFilters?.periodName === 'wavePeriod' &&
      currentFilters?.wavePeriod
    ) {
      API.getPivotDataWavePeriod(
        currentFilters?.wavePeriod,
        {
          direction: 2, // outbound
          branchIds: currentFilters?.branches,
          operatorId: currentFilters?.operator?.id,
          sections: currentFilters?.sections,
        },
        { silent: currentFilters?.autoRefresh },
        ({ pivotData, branchCounters, userCounters, sectionCounters }) => {
          setPivotData(
            pivotData?.map((x) => ({
              ...x,
              callOutcome:
                intl.messages[x?.callOutcome?.toUpperCase()] || x?.callOutcome,
              answered: x?.answered
                ? intl.messages.ANSWERED
                : intl.messages.NOT_ANSWERED,
              callHour: !isNaN(x?.callHour * 60 * 60)
                ? x?.callHour * 60 * 60
                : x?.callHour,
            }))
          );
          setBranchCounters(branchCounters);
          setUserCounters(userCounters);
          setSectionCounters(sectionCounters);
          localStorage.setItem(
            'outboundPivotFilters',
            JSON.stringify(currentFilters)
          );
        },
        (err) => {
          setOpenSnackbarAdvise({
            message: intl.messages.ERROR_REPORT_LOADING,
            severity: 'error',
          });
          console.error(err);
        }
      );
      return;
    }

    API.getPivotData(
      formatPayloadDate(start)?.toISOString(),
      formatPayloadDate(end)?.toISOString(),
      currentFilters?.grouping?.id,
      {
        direction: 2, // outbound
        branchIds: currentFilters?.branches,
        operatorId: currentFilters?.operator?.id,
        sections: currentFilters?.sections,
      },
      { silent: currentFilters?.autoRefresh },
      ({ pivotData, branchCounters, userCounters, sectionCounters }) => {
        setPivotData(
          pivotData?.map((x) => ({
            ...x,
            callOutcome:
              intl.messages[x?.callOutcome?.toUpperCase()] || x?.callOutcome,
            answered: x?.answered
              ? intl.messages.ANSWERED
              : intl.messages.NOT_ANSWERED,
            callHour: !isNaN(x?.callHour * 60 * 60)
              ? x?.callHour * 60 * 60
              : x?.callHour,
          }))
        );
        setBranchCounters(branchCounters);
        setUserCounters(userCounters);
        setSectionCounters(sectionCounters);
        localStorage.setItem(
          'outboundPivotFilters',
          JSON.stringify(currentFilters)
        );
      },
      (err) => {
        setOpenSnackbarAdvise({
          message: intl.messages.ERROR_REPORT_LOADING,
          severity: 'error',
        });
        console.error(err);
      }
    );
  }, [currentFilters, sectionsOptions]);

  const toggleChartView = () => {
    setChartView(!chartView);
    localStorage.setItem('outboundPivotChartView', JSON.stringify(!chartView));
  };

  return (
    <FullPageFlexBox flexDirection="column" overflow="hidden">
      <FiltersNavbar
        hideBreadcrumbs
        title="OUTBOUND_CALLS_REPORT"
        toggle={handleToggleNavbar}
        open={navbarOpen}
        currentFilters={currentFilters}
        setCurrentFilters={setCurrentFilters}
        isDataLimited
        sectionsOptions={sectionsOptions}
        switchValue={chartView}
        switchLabel={
          !chartView ? intl.messages.PIVOT_VIEW : intl.messages.CHART_VIEW
        }
        onSwitchClick={isOperator ? undefined : toggleChartView}
        isOperator={isOperator}
        wavePeriodOption
      />
      <FullSizeColumn overflow="auto" padding="24px">
        <FullSizeColumn
          display={!chartView ? 'none!important' : 'inherit!important'}
          gap="24px"
        >
          <CountersWrapper
            branchCounters={branchCounters}
            userCounters={userCounters}
            sectionCounters={sectionCounters}
          />
        </FullSizeColumn>
        <FullSizeColumn
          display={!chartView ? 'flex!important' : 'none!important'}
        >
          <PivotWrapper
            pivotType={2}
            data={pivotData}
            visible={!chartView}
            slice={DEFAULT_SLICE_CONFIGURATION_OUTBOUND}
            getMetadata={getMetadataOutbound}
            localStorageKey="pivotConfiguration"
            userRoles={userRoles}
          />
        </FullSizeColumn>
      </FullSizeColumn>
    </FullPageFlexBox>
  );
};

export default PivotOutboundReportPage;
